import { AlertTriangle, CheckCircle2, Circle, Loader2, MinusCircle, Undo2, XCircle } from 'lucide-react'

import { cn } from 'lib/utils'
import type { AiPlanView } from 'services/aiService'

import { describeStep, progressOf, sentenceText, type StepProgress } from '../plan/planSteps'

/**
 * A run, step by step (MYD-624): pending, running, done, skipped or failed,
 * with why a step did not run. After an undo, what undo did with each step.
 *
 * The run is followed by polling GET /ai/plans/:id (PlanCard): the executor
 * saves each result as it lands, so each poll moves the list on. The issue
 * offered SSE or polling; a run is a few seconds, and polling needs nothing
 * new on the server.
 *
 * The list is a live region, so a screen reader hears each step finish.
 */

const LOOK: Record<StepProgress, { icon: typeof Circle; tone: string; word: string }> = {
	pending: { icon: Circle, tone: 'text-gray-300', word: 'Waiting' },
	running: { icon: Loader2, tone: 'text-primary animate-spin', word: 'Running' },
	done: { icon: CheckCircle2, tone: 'text-success', word: 'Done' },
	failed: { icon: XCircle, tone: 'text-destructive', word: 'Failed' },
	skipped: { icon: MinusCircle, tone: 'text-gray-400', word: 'Skipped' },
}

const UNDO_WORDS: Record<string, string> = {
	undone: 'Undone',
	partial: 'Partly undone',
	kept: 'Kept',
	none: 'Nothing to undo',
	failed: 'Could not undo',
}

export function ExecutionProgress({ view }: { view: AiPlanView }) {
	const progress = progressOf(view)
	const results = new Map(view.results.map(result => [result.stepId, result]))
	const undo = new Map((view.undoResults ?? []).map(outcome => [outcome.stepId, outcome]))
	const refs = view.refs ?? {}

	return (
		<ol className="space-y-1.5" aria-live="polite" aria-label="Progress">
			{view.plan.steps.map((step, index) => {
				const state = progress[step.id]
				const look = LOOK[state]
				const result = results.get(step.id)
				const undone = undo.get(step.id)
				const sentence = sentenceText(describeStep(step, view.plan, refs))
				return (
					<li key={step.id} className="flex items-start gap-2 text-sm" aria-label={`Step ${index + 1}: ${look.word}. ${sentence}`}>
						<look.icon className={cn('mt-0.5 h-4 w-4 shrink-0', look.tone)} aria-hidden="true" />
						<div className="min-w-0 flex-1">
							<p className={cn('text-gray-800', state === 'skipped' && 'text-gray-500')}>{sentence}</p>
							{result?.status !== 'done' && result?.error && <p className="text-xs text-destructive">{result.error}</p>}
							{/* Done, with a caveat (MYD-627): e.g. a deck the builder shortened. */}
							{result?.status === 'done' &&
								result.warnings?.map(warning => (
									<p key={warning} className="flex items-start gap-1 text-xs text-gray-700">
										<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
										<span>{warning}</span>
									</p>
								))}
							{undone && (
								<p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
									<Undo2 className="h-3 w-3 shrink-0" aria-hidden="true" />
									<span>
										<span className="font-medium">{UNDO_WORDS[undone.outcome] ?? undone.outcome}</span>
										{undone.reason ? `: ${undone.reason}` : ''}
									</span>
								</p>
							)}
						</div>
					</li>
				)
			})}
		</ol>
	)
}

export default ExecutionProgress
